import {
  Center,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { AdminUsersTable } from '../AdminUsersTable';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { Trans } from '@lingui/react/macro';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import { MAX_SEARCH_LENGTH } from '@duke-hq/shared-constants/searchCaps';
import { usePagination } from '@duke-hq/ui/components/hooks/usePagination';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AddNewAdminUserModal } from '../AddNewAdminUserModal';
import { CenteredSpinner } from '@duke-hq/app-admin-web/components/Loading';
import AdminUserDetails from '../AdminUserDetails';
import { Pagination } from '@duke-hq/ui/components/Pagination/Pagination';
import { testIds } from './testIds';

import { SortFiltersSchema, type SortFilters } from '../types';

export function AdminUsersPage() {
  const { currentPage, pageSize, setPage } = usePagination();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const querySearch = (searchParams.get('search') ?? '').trim();
  const { userId } = useParams<{ userId: string }>();

  const { isOpen } = useDisclosure({
    isOpen: Boolean(userId),
  });

  const initialFilters = useMemo(() => {
    const sort = searchParams.get('sort') ?? undefined;
    const orderBy = searchParams.get('orderBy') ?? undefined;
    const role = searchParams.get('role') ?? undefined;
    return SortFiltersSchema.safeParse({ role, orderBy, sort }).data;
  }, [searchParams]);

  const [filters, setFilters] = useState<SortFilters>(
    initialFilters ?? {
      role: undefined,
      orderBy: undefined,
      sort: undefined,
    },
  );

  const {
    data: users,
    isFetching,
    refetch,
  } = trpcAdmin.users.allWithPagination.useQuery({
    search: querySearch,
    filters,
    pagination: {
      page: Number(currentPage),
      limit: pageSize,
    },
  });

  const handleFilterChange = useCallback(
    (newFilters: SortFilters) => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        ...newFilters,
      }));

      setPage(1);
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);

          Object.entries(newFilters).forEach(([key, value]) => {
            if (value?.length > 0) {
              nextParams.set(key, value);
            } else {
              nextParams.delete(key);
            }
          });

          return nextParams;
        },
        { replace: true },
      );
    },
    [setPage, setSearchParams],
  );

  const onClose = useCallback(
    () =>
      navigate({
        pathname: `/users`,
        search: location.search,
      }),
    [navigate],
  );

  return (
    <>
      {userId !== undefined ? (
        <Drawer isOpen={isOpen} onClose={onClose} size="xl">
          <DrawerOverlay />
          <DrawerContent>
            <Suspense fallback={<CenteredSpinner />}>
              <AdminUserDetails />
            </Suspense>
          </DrawerContent>
        </Drawer>
      ) : null}
      <TableViewLayout
        pageHeader={
          <PageHeading data-testid={testIds.TableHeading}>
            <Trans>Users</Trans>
          </PageHeading>
        }
        pageActions={<AddNewAdminUserModal onSubmit={refetch} />}
        filters={<SearchInput maxW="search-input" maxLength={MAX_SEARCH_LENGTH} />}
        tableActionsLeft={
          <Text fontSize="md" fontWeight="semibold" color="fg.subtle">
            {users?.items?.length ?? 0} users
          </Text>
        }
        table={
          isFetching ? (
            <Center p={8}>
              <Spinner size="xl" transitionDelay={'2900ms'} />
            </Center>
          ) : users != null && users?.items.length > 0 ? (
            <AdminUsersTable users={users?.items ?? []} onFilters={handleFilterChange} />
          ) : (
            <Center p={8}>
              <Text textAlign="center">
                <Trans>No users found</Trans>
              </Text>
            </Center>
          )
        }
        pagination={
          users?.items !== undefined && users.items.length > 0 ? (
            <Pagination pagination={users.pagination} onPageChange={setPage} />
          ) : null
        }
      />
    </>
  );
}
